import { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  rating: z.number().int().min(1, "Please pick a rating").max(10),
  feedback_text: z.string().max(1000, "Max 1000 characters").optional(),
  testimonial_text: z.string().max(500, "Max 500 characters").optional(),
  allow_public_display: z.boolean(),
});

export type FeedbackSource = "designer" | "mobile_results" | "settings" | "direct";

interface Props {
  source: FeedbackSource;
  relatedDesignId?: string | null;
  userId: string;
  userEmail?: string | null;
  userName?: string | null;
  onSubmitted?: () => void;
}

export const FeedbackForm = ({
  source,
  relatedDesignId,
  userId,
  userEmail,
  userName,
  onSubmitted,
}: Props) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [testimonial, setTestimonial] = useState("");
  const [allowPublic, setAllowPublic] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({
      rating,
      feedback_text: feedback.trim() || undefined,
      testimonial_text: testimonial.trim() || undefined,
      allow_public_display: allowPublic,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Invalid input");
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from("feedback_submissions")
        .insert({
          user_id: userId,
          rating: parsed.data.rating,
          feedback_text: parsed.data.feedback_text ?? null,
          testimonial_text: parsed.data.testimonial_text ?? null,
          allow_public_display: parsed.data.allow_public_display,
          source,
          related_design_id: relatedDesignId ?? null,
          user_email: userEmail ?? null,
          user_name: userName ?? null,
        })
        .select("id")
        .single();

      if (error) throw error;

      // Fire-and-forget admin notification
      supabase.functions
        .invoke("notify-feedback-submission", { body: { feedbackId: data.id } })
        .catch((err) => console.error("notify-feedback failed", err));

      toast.success("Thank you for your feedback!");
      onSubmitted?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to submit";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label className="text-base font-semibold mb-3 block">
          How would you rate your experience? <span className="text-destructive">*</span>
        </Label>
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Rating from 1 to 10">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
            const active = (hover || rating) >= n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                aria-label={`${n} of 10`}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className="p-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
              >
                <Star
                  className={cn(
                    "h-7 w-7 sm:h-8 sm:w-8 transition-colors",
                    active ? "fill-primary text-primary" : "text-muted-foreground/40"
                  )}
                />
              </button>
            );
          })}
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          {rating > 0 ? `${rating}/10` : "Tap a star to rate"}
        </p>
      </div>

      <div>
        <Label htmlFor="fb-feedback" className="text-base font-semibold mb-2 block">
          Feedback (optional)
        </Label>
        <Textarea
          id="fb-feedback"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value.slice(0, 1000))}
          placeholder="What did you like? What could be better?"
          className="min-h-[100px]"
        />
        <p className="text-xs text-muted-foreground text-right mt-1">{feedback.length}/1000</p>
      </div>

      <div>
        <Label htmlFor="fb-testimonial" className="text-base font-semibold mb-2 block">
          Testimonial (optional)
        </Label>
        <Textarea
          id="fb-testimonial"
          value={testimonial}
          onChange={(e) => setTestimonial(e.target.value.slice(0, 500))}
          placeholder="A short quote we could share with future users…"
          className="min-h-[80px]"
        />
        <p className="text-xs text-muted-foreground text-right mt-1">{testimonial.length}/500</p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={allowPublic}
          onCheckedChange={(v) => setAllowPublic(v === true)}
          className="mt-0.5"
        />
        <span className="text-sm text-muted-foreground">
          I allow Decor Canvas to display my testimonial publicly (with my first name).
        </span>
      </label>

      <Button
        type="submit"
        disabled={submitting || rating === 0}
        className="w-full h-12 text-base"
      >
        {submitting ? "Submitting…" : "Submit Feedback"}
      </Button>
    </form>
  );
};
